// ─────────────────────────────────────────────
// analytics/attribution.js
// UTM attribution capture for the resident web app.
//
// First-touch model with a 30-day window. Carries marketing attribution from
// the static marketing site (/website) handoff (see public/website/site.js)
// into booking records, so a campaign can be tied to a confirmed booking.
//
// Non-sensitive client marketing data only — never server-authoritative (unlike
// LEU). Values are untrusted text: trimmed, length-capped, stored/sent only,
// never rendered or executed. Never throws into the app.
// ─────────────────────────────────────────────

const STORAGE_KEY = "ld_app_attribution";
const UTM_FIELDS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
// referred_by_code rides the same first-touch record: it is the neighbor's
// referral code from a ?ref= share link, recorded on the booking so referrals
// can be measured. Same trust class as utm_* (untrusted text, stored only).
const ATTR_FIELDS = [...UTM_FIELDS, "landing_referrer", "referred_by_code"];
const WINDOW_MS = 30 * 24 * 60 * 60 * 1000; // 30-day first-touch window

/** Referral codes are generated from an unambiguous [A-Z2-9] alphabet. Sanitize
 *  the inbound ?ref= to that shape so nothing else is ever stored or resolved. */
function cleanRefCode(v) {
  if (!v) return null;
  const c = String(v).toUpperCase().replace(/[^A-Z2-9]/g, "").slice(0, 12);
  return c || null;
}

/** Read the stored first-touch record, clearing it if outside the window. */
function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const rec = JSON.parse(raw);
    if (!rec || !rec.captured_at) return null;
    if (Date.now() - new Date(rec.captured_at).getTime() > WINDOW_MS) {
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
      return null;
    }
    return rec;
  } catch (e) {
    return null;
  }
}

/**
 * Parse UTM params from the current URL and persist the FIRST non-empty set
 * (first-touch) for up to 30 days. Snapshots the external referrer alongside.
 * Strips UTM params from the URL afterward (mirrors the Stripe-param cleanup in
 * App.modular.js). Safe to call once on app boot.
 */
export function captureAttribution() {
  try {
    const params = new URLSearchParams(window.location.search || "");
    const fromUrl = {};
    let hasAny = false;
    UTM_FIELDS.forEach((f) => {
      const v = params.get(f);
      const clean = v ? String(v).trim().slice(0, 120) : "";
      fromUrl[f] = clean || null;
      if (clean) hasAny = true;
    });

    // Referral code from a neighbor's ?ref= share link. Triggers first-touch
    // capture on its own, even when no utm_* params are present.
    const refCode = cleanRefCode(params.get("ref"));
    if (refCode) hasAny = true;

    if (hasAny && !readStored()) {
      let referrer = null;
      try {
        const ref = document.referrer || "";
        if (ref && new URL(ref).host !== window.location.host) referrer = ref.slice(0, 300);
      } catch (e) { /* ignore malformed referrer */ }
      const rec = { ...fromUrl, landing_referrer: referrer, referred_by_code: refCode, captured_at: new Date().toISOString() };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(rec)); } catch (e) {}
    }

    // Clean the address bar regardless, so utm_* / ref don't linger or get shared.
    if (hasAny) {
      UTM_FIELDS.forEach((f) => params.delete(f));
      params.delete("ref");
      const qs = params.toString();
      const url = window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
      try { window.history.replaceState({}, "", url); } catch (e) {}
    }
  } catch (e) {
    // Attribution must never break the app.
  }
}

/**
 * Return the current first-touch attribution (within the 30-day window) as a
 * flat object of utm_* + landing_referrer fields, or all-null when none/expired.
 * Shape matches the requests attribution columns for a direct payload spread.
 */
export function getAttribution() {
  const rec = readStored();
  const out = {};
  ATTR_FIELDS.forEach((f) => { out[f] = (rec && rec[f]) || null; });
  return out;
}

/**
 * Clear stored attribution after a confirmed booking so a later direct visit is
 * not mis-attributed to a prior campaign.
 */
export function clearAttribution() {
  try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
}
